import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import {
  getAgentBalances,
  type AssetBalance,
  type ChainBalance,
} from './api';

// AgentBalancesCard renders the wallet's holdings across every chain
// Sigil has an RPC for. The "primary" chain (agent.config.default_chain
// → 'polygon' fallback) is open by default; the rest collapse behind a
// disclosure. A refresh button bypasses the 30s server-side cache.
export function AgentBalancesCard({ orgId, agentId }: { orgId: string; agentId: string }) {
  const qc = useQueryClient();
  const balances = useQuery({
    queryKey: ['balances', orgId, agentId],
    queryFn: () => getAgentBalances(orgId, agentId),
    enabled: !!orgId && !!agentId,
    refetchInterval: 60_000,
  });

  const refresh = useMutation({
    mutationFn: () => getAgentBalances(orgId, agentId, true),
    onSuccess: (data) => {
      qc.setQueryData(['balances', orgId, agentId], data);
    },
  });

  if (balances.isLoading) {
    return (
      <Card>
        <CardHeader title="Balances" />
        <CardBody><p className="text-sm text-ink-tertiary">Loading…</p></CardBody>
      </Card>
    );
  }
  const data = balances.data;
  if (!data || !data.has_wallet) {
    return (
      <Card>
        <CardHeader title="Balances" description="This agent has no wallet attached." />
      </Card>
    );
  }

  const allChains = data.chains ?? [];
  const primaryChain = data.default_chain ?? '';
  const primary =
    allChains.find((c) => c.chain === primaryChain) ??
    allChains.find((c) => (c.native?.raw ?? '0') !== '0' || (c.tokens?.length ?? 0) > 0) ??
    allChains[0];
  const others = allChains.filter((c) => c.chain !== primary?.chain);

  return (
    <Card>
      <CardHeader
        title="Balances"
        description={
          data.note
            ? data.note
            : `Wallet ${shortAddr(data.address ?? '')} · ${data.curve ?? ''}. Refreshes every 60s (server cache 30s).`
        }
      />
      <CardBody className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="font-mono text-[11px] text-ink-tertiary">
            updated {timeAgo(data.refreshed_at)}
          </p>
          <button
            type="button"
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="rounded border border-line bg-surface-white px-2 py-1 font-mono text-[10px] text-ink-secondary hover:border-ink-secondary disabled:opacity-50"
          >
            {refresh.isPending ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {primary && <ChainBlock chain={primary} primary />}

        {others.length > 0 && <OtherChainsDisclosure others={others} />}
      </CardBody>
    </Card>
  );
}

function OtherChainsDisclosure({ others }: { others: ChainBalance[] }) {
  const [open, setOpen] = useState(false);
  const nonEmpty = others.filter(
    (c) => (c.native?.raw ?? '0') !== '0' || (c.tokens?.length ?? 0) > 0 || c.error,
  );
  const empty = others.length - nonEmpty.length;
  return (
    <details
      className="rounded-lg border border-line-soft bg-surface-soft/40"
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2 text-sm text-ink-secondary hover:text-ink-primary">
        <span>
          {nonEmpty.length > 0
            ? `${nonEmpty.length} other chain${nonEmpty.length === 1 ? '' : 's'} with assets`
            : 'Other chains'}
          {empty > 0 && (
            <span className="ml-2 font-mono text-[10px] text-ink-tertiary">
              · {empty} empty
            </span>
          )}
        </span>
        <span className="font-mono text-[10px] text-ink-tertiary">{open ? 'hide' : 'show'}</span>
      </summary>
      <div className="space-y-3 border-t border-line-soft bg-surface-white p-3">
        {others.map((c) => (
          <ChainBlock key={c.chain} chain={c} />
        ))}
      </div>
    </details>
  );
}

function ChainBlock({ chain, primary = false }: { chain: ChainBalance; primary?: boolean }) {
  const empty = (chain.native?.raw ?? '0') === '0' && (chain.tokens?.length ?? 0) === 0;
  return (
    <div
      className={`rounded-lg border ${
        primary ? 'border-bit2me/30 bg-bit2me/5' : 'border-line-soft bg-surface-soft/30'
      } p-3`}
    >
      <div className="flex items-center justify-between">
        <p className="font-mono text-[10px] uppercase tracking-eyebrow text-ink-tertiary">
          {chain.chain}
        </p>
        {chain.error && (
          <p className="font-mono text-[10px] text-danger" title={chain.error}>
            RPC error
          </p>
        )}
      </div>
      {empty && !chain.error ? (
        <p className="mt-1 text-xs text-ink-tertiary">No balance.</p>
      ) : (
        <ul className="mt-1 space-y-1">
          {chain.native && (
            <AssetRow asset={chain.native} kind="native" />
          )}
          {(chain.tokens ?? []).map((t) => (
            <AssetRow key={t.address} asset={t} kind="token" />
          ))}
        </ul>
      )}
    </div>
  );
}

function AssetRow({ asset, kind }: { asset: AssetBalance; kind: 'native' | 'token' }) {
  return (
    <li className="flex items-baseline justify-between gap-2">
      <span className="font-mono text-[11px] text-ink-secondary">
        {asset.symbol}
        {kind === 'native' && <span className="ml-1 text-ink-tertiary">·native</span>}
      </span>
      <span className="font-mono text-sm font-semibold text-ink-primary">{asset.human}</span>
    </li>
  );
}

function shortAddr(a: string): string {
  if (a.length <= 14) return a;
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleString();
}
